/**
 * PsicoSalud — Cliente HTTP.
 *
 * Envoltorio sobre `fetch` que adjunta el token de acceso, renueva la
 * sesión de forma transparente cuando expira y normaliza los errores
 * del backend a un tipo único.
 *
 * El access token vive 30 minutos: sin renovación automática, el
 * profesional perdería el trabajo en curso a mitad de una sesión
 * clínica. La renovación se serializa en una única promesa para que
 * varias peticiones simultáneas no disparen varios refresh.
 */

import type { TokenPair } from '@/types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
export const API_PREFIX = `${BASE_URL}/api/v1`;

const ACCESS_TOKEN_KEY = 'psicosalud.access_token';
const REFRESH_TOKEN_KEY = 'psicosalud.refresh_token';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ============================================================
// Almacenamiento de tokens
// ============================================================
export const tokenStore = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  save(tokens: TokenPair): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

// ============================================================
// Renovación de sesión
// ============================================================
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const refreshToken = tokenStore.refresh;
  if (!refreshToken) return false;

  const response = await fetch(`${API_PREFIX}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    tokenStore.clear();
    return false;
  }

  tokenStore.save((await response.json()) as TokenPair);
  return true;
}

function ensureRefresh(): Promise<boolean> {
  refreshInFlight ??= refreshSession().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

// ============================================================
// Petición
// ============================================================
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Evita el intento de renovación (se usa en el propio login). */
  skipAuth?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_PREFIX}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  }
  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

async function parseError(response: Response): Promise<ApiError> {
  let detail: unknown;
  let message = `Error ${response.status}`;
  try {
    const payload = await response.json();
    detail = payload?.detail ?? payload;
    if (typeof payload?.detail === 'string') {
      message = payload.detail;
    } else if (Array.isArray(payload?.detail)) {
      // Errores de validación de FastAPI: [{loc, msg, type}, ...]
      message = payload.detail.map((e: { msg?: string }) => e.msg).filter(Boolean).join('; ');
    }
  } catch {
    message = response.statusText || message;
  }
  return new ApiError(response.status, message, detail);
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, skipAuth = false } = options;

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    const token = tokenStore.access;
    if (token && !skipAuth) headers.Authorization = `Bearer ${token}`;

    return fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  };

  let response = await send();

  // Un 401 con refresh token disponible se reintenta una sola vez.
  if (response.status === 401 && !skipAuth && tokenStore.refresh) {
    const renewed = await ensureRefresh();
    if (renewed) {
      response = await send();
    }
  }

  if (!response.ok) throw await parseError(response);

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return (await response.json()) as T;
}
